export * from "./appSession";
export * from "./regionalCountry";
export * from "./verticalBranding";
export * from "./scoreBannerEligibility";
export * from "./schoolCodes";
export {
  subscribeVideoFullscreen,
  getVideoFullscreenSnapshot,
  setVideoFullscreen,
  useVideoFullscreen,
  useSyncVideoFullscreen,
} from "./videoFullscreenStore";
export * from "./bridgeCoursePackageMapping";
export * from "./reverseGeocodeNominatim";

export function capitalize(str: string): string {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const formatDate = (
  dateStr: string,
  isFormatRequired: boolean = true,
) => {
  if (!isFormatRequired) return dateStr;
  const [year, month, day] = dateStr.split("-");
  return `${day}-${month}-${year}`;
};

export {
  getSubjectIcon,
  SUBJECT_ICON_MAP,
  getSubjectIconWithoutLabel,
  SUBJECT_ICON_WITHOUT_LABEL_MAP,
  getSubjectLabelIcon,
  SUBJECT_LABEL_ICON_MAP,
  getSubjectBadgeIconProps,
  SUBJECT_BADGE_ICON_CONFIG,
} from "./classConfig";
export { hexToRgba } from "./hexToRgba";
export { formatAmount } from "./formatAmount";

// Phone country codes for input prefix dropdown (app maps countryKey to flag image)
export type PhoneCountryOption = {
  countryCode: string;
  countryKey: string;
};

export const PHONE_COUNTRY_CODES: PhoneCountryOption[] = [
  { countryCode: "+91", countryKey: "IN" },
  { countryCode: "+971", countryKey: "AE" },
];

// Shared Route Constants
export const ROUTES = {
  AUTH: {
    LOGIN: "login",
    FORGOT_PASSWORD: "forgot-password",
    RESET_PASSWORD: "reset-password",
    WEB_VIEW: "web-view",
    INTRO: "introScreen",
    WELCOME: "welcome",
    PHONE_LOGIN: "phone-login",
    OTP_VERIFICATION: "otp-verification",
    USERNAME_LOGIN: "username-login",
    /** SCA / Admission ID login flow (school + admission ID). */
    ADMISSION_ID_FLOW: "admission-id-flow",
    ACTIVATION_CODE: "activation-code",
    SIGNUP: "signup",
    REPORTS: "reports",
  },
  APP: {
    DASHBOARD: "dashboard",
    CONTACT_US: "contact-us",
    REFER_AND_EARN: "refer-and-earn",
    SCHOOL_REPORTS: "school-reports-navigator",
    PROFILE_SETUP: "profile-setup",
    BOTTOMNAVIGATION: {
      HOME: "home",
      PLAY: "play",
      EXPLORE: "explore",
    },
    LIVE_CLASS: {
      COURSE_OVERVIEW: "mycourse",
      VIDEO_REWATCH: "rewatch",
      ASSIGNMENT_INSTRUCTIONS: "assignment-instructions",
      ASSIGNMENT_EXAM: "assignment-exam",
      ZOOM_LIVE_CLASS: "zoom-live-class",
      BRIDGE_COURSE: "bridge-course",
    },
    SUBSCRIPTIONS: "subscriptions",
    REVIEW_ORDER: "review-order",
    PAYMENT_STATUS: "payment-status",
    SMART_BATCHING: "smart-batching",
    BATCH_CONFIRMATION: "batch-confirmation",
    SCORE_PDF_VIEWER: "scorePdfViewer",
    TESTS: {
      ROOT: "tests",
      HOME: "tests-home",
      MCTS: "mcts",
      CYOT: "cyot",
      PYPS: "pyps",
      ILTS: "ilts",
      CWTS: "cwts",
      SCORE: "score",
      SCORE_REGISTER: "score-register",
      SCORE_MIND_MAPS: "score-mind-maps",
      SCORE_FORMULA_SHEETS: "score-formula-sheets",
      REVISION_TEST: "revision-tests",
      TEST_DETAIL: "test-detail",
      CYOT_FLOW: "cyotFlow",
      ANSWER_SHEET: "answer-sheet",
      UPLOAD_ANSWER_SHEET: "upload-answer-sheet",
      TEST_SUCCESS: "test-success",
      ANSWER_SHEET_VIEWER: "answer-sheet-viewer",
      GENERAL_INSTRUCTIONS: "general-instructions",
      EXAM: "exam",
      QUESTION_LIST: "question-list",
      SOLUTIONS: "solutions",
      SUBJECTIVE_SOLUTIONS: "subjective-solutions",
      CALCULATION_SCORE: "calculation-score",
      TEST_REPORT: "test-report",
      WIT: "wit",
      GENIUS_ASSESSMENT_STARTING_SOON: "genius-assessment-starting-soon",
      GENIUS_ASSESSMENT_NOT_AVAILABLE: "genius-assessment-not-available",
    },
    GENIUS_DEEPLINK: "genius-deeplink",
    WEB_VIEW: "web-view",
    DOUBTS: {
      DOUBTS: "doubts",
      ASK_QUESTION: "ask-question",
      SELECT_SUBJECT: "select-subject",
      CHAT_MODULE: "chat-module",
      TRIAL_EXPIRED: "trial-expired",
      OPEN_CAMERA: "open-camera",
      EDIT_PHOTO: "edit-photo",
      INSTRUCTIONS: "instructions",
    },
    REPORTS: "reports",
    DEFAULT: "default",
    PAID_USER_ONBOARDING: "dashboard/user-onboarding",
  },
} as const;

export const ROUTESWEB = {
  AUTH: {
    LOGIN: "login",
    SIGNUP: "signup",
    PHONE_LOGIN: "phone-login",
    OTP_VERIFICATION: "otp-verification",
    USERNAME_LOGIN: "username-login",
    FORGOT_PASSWORD: "forgot-password",
    RESET_PASSWORD: "reset-password",
    MULTIPLE_ACCOUNTS: "multiple-accounts",
    INTRO: "intro",
    ADMISSION_ID_FLOW: "admission-id-flow",
    ACTIVATION_CODE: "activation-code",
    /** Pre-auth change phone (same UX as profile flow; avoids AuthGuard on /profile routes) */
    CHANGE_PHONE_INSTRUCTIONS: "change-phone-instructions",
    CHANGE_PHONE_NEW_NUMBER: "change-phone-new-number",
    CHANGE_PHONE_VERIFY: "change-phone-verify",
  },
  APP: {
    DASHBOARD: "dashboard",
    ACTIVITIES: "activities",
    COURSES: "courses",
    CLASSES: "classes",
    AGORA: "/live-class/:class_id/student-live-class/:meeting_id",
    AGORA_WEBVIEW: "/webview/live-class",
    PLAYLIST_DETAIL: "playlist-detail",
    VIDEO_PLAYER: "video-player",
    CONTACT_US: "contact-us",
    COURSE_OVERVIEW: "mycourse",
    VIDEO_REWATCH: "rewatch",
    DOUBTS: "doubts",
    AINA_VOICE: "ainavoice",
    AINA_MENTOR: "ainamentor",
    LIVE_CLASSES: "live-classes",
    SELF_LEARN: "self-learn",
    TESTS: "tests",
    REFER_AND_EARN: "refer-and-earn",
    REFER_AND_EARN_TERMS: "refer-and-earn/terms-and-conditions",
    REFER_AND_EARN_FAQ: "refer-and-earn/faq",
    MY_REFERRALS: "my-referrals",
    BOOKMARKS: "self-learn/my-bookmarks",
    SUBSCRIPTIONS: "subscriptions",
    REVIEW_ORDER: "review-order",
    PAYMENT_STATUS: "payment-status",
    YOUTUBE_LIVECLASS: "live-classes/yt-class",
    BRIDGE_COURSE: "live-classes/bridge-course",
    PROFILE: "profile",
    PROFILE_SETTINGS: "profile/settings",
    PROFILE_SETUP: "profile-setup",
    SCHOOL_REPORTS_ACADEMIC_PROGRESS:
      "profile/school-reports/academic-progress",
    SCHOOL_REPORTS_ADD_REPORT_DETAILS:
      "profile/school-reports/add-report-details",
    SCHOOL_REPORTS_ADD_MARKS: "profile/school-reports/add-marks",
    SCHOOL_REPORTS_EDIT_REPORT: "profile/school-reports/edit-report/:reportId",
    SCHOOL_REPORTS: "profile/school-reports",
    SCHOOL_REPORT_DETAILS: "profile/school-reports/report-details",
    CHANGE_PHONE_NUMBER: "profile/change-phone-number",
    CHANGE_NUMBER_VERIFY: "profile/change-phone-number/verify",
    INSTRUCTIONS_PAGE: "profile/instructions-page",
    TERMSANDPOLICYPAGE: "profile/instructions-page/terms-and-policy",
    REPORTS: "reports",
    ASSIGNMENT_REPORT: "assignment-report",
    REPORT_TESTING: "report-testing",
    ASSIGNMENT_INSTRUCTIONS: "assignment-instructions",
    ASSIGNMENT_EXAM: "assignment-exam",
    LEARNER_PRACTICE: "learner-practice",
    SMART_BATCHING: "smart-batching",
    MASTERCLASSES: "dashboard/masterclasses",
    BATCH_CONFIRMATION: "batch-confirmation",
    SCORE_BEFORE_REGISTRATION: "scoreBeforeRegistration",
    SCORE_PDF_VIEWER: "score-pdf-viewer",
    ZOOM_LIVECLASS: "/live-class/:class_id/zoom-live-class",
    PAID_USER_ONBOARDING: "dashboard/user-onboarding",
  },
} as const;
export * from "./validation";
export * from "./usernameValidation";
export * from "./interfaces/remote-config";
export * from "./hooks";
export {
  getCountryFlagUrl,
  getCountryFlagEmoji,
  parseCountriesWithPhoneNumbers,
  resolveSelectedCountryFromApi,
  resolvePreferredCountryFromApi,
  type CountryWithOptionalFlag,
  type CountryFromApi,
  type CountriesWithPhoneNumbersApiResponse,
} from "./countryFlag";
export * from "./enums/feature-keys";
export * from "./liveClassScheduleState";
export {
  extractStudentPresence,
  getStudentPresenceMode,
  getStudentPresenceLabel,
  getStudentPresenceIconName,
  isStudentPresenceOffline,
  type StudentPresenceMode,
} from "./liveClassPresence";
export {
  extractLiveClassesFromApiResponse,
  getLiveClassRowId,
  mergeLiveClassRows,
} from "./extractLiveClassesFromApiResponse";
export {
  registerRemoteConfigService,
  getRemoteConfigService,
} from "./services/remote-config-registry";
export {
  UAE_ISD_DIGITS,
  INDIA_ISD_DIGITS,
  normalizeIsdCode,
  isUaeCountryCode,
  isIndiaCountryCode,
  formatOtpCountdownTimer,
  getInitialsFromName,
  isRemoteProfilePhotoUrl,
  buildDisplayName,
  shouldSkipDuplicateInitialOtpSend,
  defaultOtpModeForCountry,
  parseUnifiedOtpFlowError,
  type OtpMode,
} from "./otpPhone";
export {
  CUSTOMER_SUPPORT_NUMBERS_RC_KEY,
  normalizeDialCode,
  resolveSupportPhoneNumber,
  formatSupportDisplayPhone,
  getCustomerSupportNumbers,
  type CustomerSupportNumber,
} from "./customerSupportPhone";
export { shouldShowHeaderAvatarMenuItem } from "./headerAvatarMenu";
export * from "./constants";
export * from "./gatewayRegion";
export * from "./menuNavigationVisibility";
export {
  formatDuration,
  formatTimestampInTimeZone,
  formatCompactTimestampInTimeZone,
  formatTimestampWithOrdinalInTimeZone,
  formatDateInTimeZone,
  formatClockTimeInTimeZone,
  formatUtcClockTimeInTimeZone,
  formatUtcScheduleSlotInTimeZone,
  createStudentLocalTimeHelpers,
  parseBackendTimestampToDate,
  normalizeTimezoneIdentifier,
  type BackendTimestamp,
  type StudentLocalTimeOptions,
  type StudentLocalTimeHelperConfig,
} from "./time";
export {
  LOW_ORDER_BILLING_LOOKUP_LIMIT_RS,
  LOW_ORDER_DEFAULT_BILLING_EMAIL,
  buildBillingAddressFromLocationParts,
  formatPhoneForStateLookup,
  hasSavedBillingLocation,
  isIndianUser,
  lookupPhoneState,
  resolveLowOrderBillingEmail,
  type PhoneStateLookupResult,
} from "./phoneStateLookup";
export * from "./aina/examPreference";
export * from "./aina/examDetailsMeta";
export * from "./aina/ainaSessionUtils";
export * from "./aina/ainaWidgetEligibility";
export * from "./aina/ainaChromeVisibility";
export * from "./aina/ainaEventTypes";
export * from "./aina/ainaFlowAction";
export * from "./aina/ainaCameraDoubtOnboarding";
export * from "./aina/ainaNativeLaunchProps";
export * from "./aina/ainaRemoteFeatureFlags";
export * from "./aina/ainaLearnFeaturesEnable";
export * from "./aina/ainaTenant";
export * from "./aina/ainaReturnPath";
export * from "./aina/ainaBreadcrumbSource";
export * from "./api/omsBaseUrls";
export * from "./api/gatewayBaseUrls";
export * from "./api/commsBaseUrls";
export * from "./api/lsqBaseUrls";
export * from "./signup";
export {
  getProgramCohortHistory,
  type ProgramCohortHistory,
} from "./getProgramCohortHistory";
export * from "./profileExamCategoryEligibility";
